import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  FinancialSubprojectSelectorDocument,
  ImputableSubprojectSelectorDocument,
  SubprojectForSelectFragment,
  SubprojectSelectorDocument,
  SubprojectSelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import i18n from 'modules/core/lib/i18n'

interface CommonProps {
  value: SubprojectForSelectFragment | null
  onChange: (value: SubprojectForSelectFragment | null) => void
  disabled?: boolean
  hideSearchIcon?: boolean
  label?: string
  onlyActive?: boolean
  notClosed?: boolean
}

interface SubprojectSelectorProps extends CommonProps {
  projectId: number
  imputableProjectId?: never
  financialProjectId?: never
}

interface ImputableSubprojectSelectorProps extends CommonProps {
  projectId?: never
  imputableProjectId: number
  financialProjectId?: never
}

interface FinancialSubprojectSelectorProps extends CommonProps {
  projectId?: never
  imputableProjectId?: never
  financialProjectId: number
}

const SubprojectItem = ({ subproject }: { subproject: SubprojectForSelectFragment }) => {
  return (
    <Box gap="s2">
      <Text.Normal weight="medium" color="grey1000" ellipsis>
        {subproject.name}
      </Text.Normal>
    </Box>
  )
}

export const SubprojectSelector: React.FC<
  SubprojectSelectorProps | ImputableSubprojectSelectorProps | FinancialSubprojectSelectorProps
> = ({
  projectId,
  imputableProjectId,
  financialProjectId,
  value,
  onChange,
  disabled,
  hideSearchIcon,
  label,
  onlyActive,
  notClosed,
}) => {
  const extractConnection = (data: SubprojectSelectorQuery) => {
    return data.projectManagement.subprojectsConnection
  }

  const renderEntity = (entity: SubprojectForSelectFragment) => {
    return <SubprojectItem subproject={entity} />
  }

  const imputableSelected = !!imputableProjectId
  const financialSelected = !!financialProjectId

  if (imputableSelected) {
    const variables = {
      imputableProjectId,
      onlyActive,
      notClosed,
    }

    const document = ImputableSubprojectSelectorDocument

    return (
      <EntitySelector
        document={document}
        extractConnection={extractConnection}
        label={label ?? i18n.t('project_management.modals.add_project.select_subproject')}
        onChange={onChange}
        renderEntity={renderEntity}
        renderValue={(entity) =>
          entity !== null ? (
            <Text.Normal ellipsis>{entity.name}</Text.Normal>
          ) : (
            <Text.Normal>{i18n.t('common.select')}</Text.Normal>
          )
        }
        value={value}
        variables={variables}
        disabled={disabled}
        hideSearchIcon={hideSearchIcon}
      />
    )
  }

  if (financialSelected) {
    const variables = {
      financialProjectId,
      onlyActive,
      notClosed,
    }

    const document = FinancialSubprojectSelectorDocument

    return (
      <EntitySelector
        document={document}
        extractConnection={extractConnection}
        label={label ?? i18n.t('project_management.modals.add_project.select_subproject')}
        onChange={onChange}
        renderEntity={renderEntity}
        renderValue={(entity) =>
          entity !== null ? (
            <Text.Normal ellipsis>{entity.name}</Text.Normal>
          ) : (
            <Text.Normal>{i18n.t('common.select')}</Text.Normal>
          )
        }
        value={value}
        variables={variables}
        disabled={disabled}
        hideSearchIcon={hideSearchIcon}
      />
    )
  }

  if (projectId) {
    const variables = {
      projectId,
      onlyActive,
      notClosed,
    }

    const document = SubprojectSelectorDocument

    return (
      <EntitySelector
        document={document}
        extractConnection={extractConnection}
        label={label ?? i18n.t('project_management.modals.add_project.select_subproject')}
        onChange={onChange}
        renderEntity={renderEntity}
        renderValue={(entity) =>
          entity !== null ? (
            <Text.Normal ellipsis>{entity.name}</Text.Normal>
          ) : (
            <Text.Normal>{i18n.t('common.select')}</Text.Normal>
          )
        }
        value={value}
        variables={variables}
        disabled={disabled}
        hideSearchIcon={hideSearchIcon}
      />
    )
  }
}
